/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, VFlow } from 'bold-ui'
import { booleanToSimNaoEnum, simNaoEnumRecord } from 'components/form/field/SimNaoRadioGroupField'
import { HLabel } from 'components/HLabel'
import { isAfter } from 'date-fns'
import {
  useAcompanhamentoPreNatalCidadaoQuery,
  useDataUltimaConsultaOdontoGestanteQuery,
} from 'graphql/hooks.generated'
import { AtendimentoProfissional } from 'graphql/types.generated'
import { tipoSanguineo } from 'types/enums'
import { calculateIdadeGestacional } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { calculateDpp } from 'util/date/calculateDpp'
import { dateAsDdMmYyyy, formatDate, toDate } from 'util/date/formatDate'
import { ImprimirPreNatalButton } from 'view/atendimentos/detail/soap/aside/pre-natal/components/ImprimirPreNatalButton'
import { IdadeGestacionalAcompanhamentoPreNatal } from 'view/atendimentos/detail/soap/aside/pre-natal/model-acompanhamentoPreNatal'
import { RiscoGravidezTag } from 'view/atendimentos/detail/soap/pre-natal/components/RiscoGravidezTag'
import { formatIdadeGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'

interface InformacoesGestacaoAtualCardProps {
  cidadaoId: ID
  atendimentoId: ID
  loading: boolean
  dum: Date
  idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal
  dataInicioGestacao: Date
  dppEcografica: Date
  atendimentosProfUltimaGestacao: AtendimentoProfissional[]
  dataReferencia: Instant
}

export function InformacoesGestacaoAtualCard(props: InformacoesGestacaoAtualCardProps) {
  const {
    cidadaoId,
    atendimentoId,
    loading,
    dum,
    idadeGestacional,
    dataInicioGestacao,
    dppEcografica,
    atendimentosProfUltimaGestacao,
    dataReferencia,
  } = props

  const {
    data: { cidadao: { prontuario = null, tipoSanguineo: tipoSanguineoCidadao } = {} },
    loading: loadingCidadao,
  } = useAcompanhamentoPreNatalCidadaoQuery({ variables: { id: cidadaoId }, fetchPolicy: 'cache-first' })

  const prontuarioId = prontuario?.id

  const { dpp: dppCronologica } = calculateDpp(dum)

  const consultasValidas = atendimentosProfUltimaGestacao?.filter((atendProf: AtendimentoProfissional) =>
    isAfter(atendProf.iniciadoEm, dataInicioGestacao)
  )

  const quantidadeConsultas = consultasValidas?.length
  const dataPrimeiraConsulta = consultasValidas?.first()?.iniciadoEm
  const dataUltimaConsulta = consultasValidas?.last()?.iniciadoEm
  const igPrimeiraConsulta = calculateIdadeGestacional(dataInicioGestacao, toDate(dataPrimeiraConsulta))

  const {
    data: { dataUltimaConsultaOdontoGestante },
    loading: loadingDataOdonto,
  } = useDataUltimaConsultaOdontoGestanteQuery({
    variables: { input: { prontuarioId, cidadaoId, dataInicioGestacao: dataInicioGestacao } },
    skip: !prontuarioId || !dataInicioGestacao,
    fetchPolicy: 'cache-first',
  })

  const headerContent = (
    <HFlow justifyContent='space-between' alignItems='center'>
      <RiscoGravidezTag isAltoRisco={prontuario?.preNatalAtivo?.altoRisco} />
      <ImprimirPreNatalButton
        atendimentoId={atendimentoId}
        prontuarioId={prontuarioId}
        dataAtendimento={dataReferencia}
      />
    </HFlow>
  )

  return (
    <AcompanhamentoCard
      id='informacoes-gestacao-atual'
      title='Informações da gestação atual'
      loading={loading || loadingCidadao || loadingDataOdonto}
      headerContent={headerContent}
      headerStyle={css`
        grid-template-columns: auto 1fr;
        padding: 0.25rem 1rem;
      `}
    >
      <HFlow
        justifyContent='space-between'
        style={css`
          padding: 0.5rem;
        `}
      >
        <VFlow vSpacing={0.5}>
          <HLabel title='DUM:'>{dateAsDdMmYyyy(dum)}</HLabel>
          <HLabel title='IG (DUM):'>{formatIdadeGestacional(idadeGestacional?.cronologicaEmDias)}</HLabel>
          <HLabel title='DPP (DUM):'>{dateAsDdMmYyyy(dppCronologica)}</HLabel>
          <HLabel title='IG 1ª consulta:'>{formatIdadeGestacional(igPrimeiraConsulta.emDias)}</HLabel>
        </VFlow>
        <VFlow vSpacing={0.5}>
          <HLabel title='IG (eco. obst.):'>{formatIdadeGestacional(idadeGestacional?.ecograficaEmDias)}</HLabel>
          <HLabel title='DPP (eco. obst.):'>{dppEcografica && dateAsDdMmYyyy(dppEcografica)}</HLabel>
          <HLabel title='Tipo de gravidez:'>{prontuario?.preNatalAtivo?.tipoGravidez?.descricao}</HLabel>
          <HLabel title='Gravidez planejada:'>
            {simNaoEnumRecord[booleanToSimNaoEnum(prontuario?.preNatalAtivo?.gravidezPlanejada)]}
          </HLabel>
        </VFlow>
        <VFlow vSpacing={0.5}>
          <HLabel title='Consultas:'>
            {quantidadeConsultas} | Última em {formatDate(dataUltimaConsulta)}
          </HLabel>
          <HLabel title='Última consulta odontológica:'>
            {dataUltimaConsultaOdontoGestante && formatDate(dataUltimaConsultaOdontoGestante)}
          </HLabel>
          <HLabel title='Tipo sanguíneo:'>{tipoSanguineoCidadao && tipoSanguineo[tipoSanguineoCidadao]}</HLabel>
        </VFlow>
      </HFlow>
    </AcompanhamentoCard>
  )
}
